import { QueueMetric, QueueMetrics } from 'lib-dashboard-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { WebSocketRealTimeQueueDashboardMetricsChangedInboundMessage } from 'middleware/webSocket';
import { pubSub } from 'utils/pubSub';

import { aggregateQueueMetrics } from './aggregateQueueMetrics/aggregateQueueMetrics';
import { mergeQueueMetrics } from './mergeQueueMetrics/mergeQueueMetrics';

type UseAggregateQueueMetricsOptions = {
  /** The full queue metrics from the API response. */
  queueMetrics: readonly QueueMetrics[] | undefined;
  /** The queue IDs to include in the aggregation. */
  queueIds: readonly string[];
};

type UseAggregateQueueMetricsResult = {
  /** The aggregated metrics across all queues in scope. */
  aggregateMetrics: QueueMetric[] | undefined;
};

/**
 * A hook that computes aggregate metrics across all queues in scope by
 * combining individual queue metrics into a single set. It handles real-time
 * WebSocket updates by maintaining an internal map of per-queue metrics,
 * merging partial updates, and recomputing the aggregate.
 */
export const useAggregateQueueMetrics = ({
  queueMetrics,
  queueIds,
}: UseAggregateQueueMetricsOptions): UseAggregateQueueMetricsResult => {
  // Internal mutable map tracking the latest metrics per queue ID.
  const metricsMapRef = useRef<Map<string, QueueMetrics>>(new Map());

  const [aggregateMetrics, setAggregateMetrics] = useState<
    QueueMetric[] | undefined
  >(undefined);

  // Recompute from the internal map.
  const recompute = useCallback(() => {
    const allMetrics = Array.from(metricsMapRef.current.values());

    if (allMetrics.length === 0) {
      setAggregateMetrics(undefined);
    } else {
      setAggregateMetrics(aggregateQueueMetrics(allMetrics));
    }
  }, []);

  // When the API response changes, reset the internal map and recompute.
  useEffect(() => {
    if (!queueMetrics) {
      metricsMapRef.current.clear();
      setAggregateMetrics(undefined);
      return;
    }

    const newMap = new Map<string, QueueMetrics>();
    for (const qm of queueMetrics) {
      if (queueIds.includes(qm.id)) {
        newMap.set(qm.id, qm);
      }
    }
    metricsMapRef.current = newMap;
    recompute();
  }, [queueMetrics, queueIds, recompute]);

  // Merge partial WebSocket updates into the internal map.
  const handleMetricsChanged = useCallback(
    (payload: WebSocketRealTimeQueueDashboardMetricsChangedInboundMessage) => {
      let hasChanges = false;

      for (const partialQueue of payload.params.queue_metrics) {
        if (!queueIds.includes(partialQueue.id)) continue;

        const existing = metricsMapRef.current.get(partialQueue.id);
        if (!existing) continue;

        const updatedMetrics = mergeQueueMetrics(
          existing.metrics,
          partialQueue.metrics,
        );

        metricsMapRef.current.set(partialQueue.id, {
          ...existing,
          metrics: updatedMetrics,
        });
        hasChanges = true;
      }

      if (hasChanges) {
        recompute();
      }
    },
    [queueIds, recompute],
  );

  // Ref to ensure the latest handler is used in the subscription callback.
  const handleMetricsChangedRef = useRef<typeof handleMetricsChanged | null>(
    null,
  );
  useEffect(() => {
    handleMetricsChangedRef.current = handleMetricsChanged;
  }, [handleMetricsChanged]);

  // Subscribe to WebSocket queue metrics change events.
  useEffectOnce(() => {
    const unsubscribe = pubSub.subscribe(
      'real_time_dashboard:queue_metrics_changed',
      (payload) => {
        handleMetricsChangedRef.current?.(payload);
      },
    );

    return () => unsubscribe();
  });

  return useMemo(() => ({ aggregateMetrics }), [aggregateMetrics]);
};
